import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
} from "@mui/material";
import ArtworkPicker from "./ArtworkPicker";
import { PortfolioArtwork, Portfolio, PortfolioDto, PortfolioUpdateDto } from "../../models/Portfolio";

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { useApiClient } from '../../components/useApiClient';

//Error Handling
import { useErrorHandler } from '../../hooks/useErrorHandler';
import ErrorSummary from '../ErrorHandler/ErrorSummary';

//Permissions Dialog
import PortfolioAccessDialog from "./PortfolioAccessDialog";
import UserManagementAccessDialog from "../AccessManagement/UserAccessManagementDialog";

import type { ArtworkStub } from "../../models/Artwork";
export interface PortfioDialgProps {
    open: boolean;
    onClose: () => void;
    portfolio: PortfolioDto | null;
    onSaved: () => Promise<void>;
}
const PortfolioDialog: React.FC<PortfioDialgProps> = ({ open, onClose, portfolio, onSaved }) => {

    const { errors, clearErrors, validateRequired } = useErrorHandler();
    const [accessOpen, setAccessOpen] = useState(false);

    //Support API Calls
    const { get, putJson, postJson } = useApiClient();
    const [isLoading, setIsLoading] = useState(false);
    const { activeClientId, getAccessToken } = useUser();

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const [name, setName] = useState(portfolio?.name || "");
    const [description, setDescription] = useState(portfolio?.description || "");
    const [selectedArtworkIds, setSelectedArtworkIds] = useState<number[]>([])
    const [selectedArtworks, setSelectedArtworks] = useState<PortfolioArtwork[] | undefined>([]);
    const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio>();
    const [artworks, setArtworks] = useState<ArtworkStub[]>([]);
    const [error, setError] = useState<string | null>(null);

    //API Calls
    // Type guard for Portfolio
    function isPortfolio(obj: any): obj is Portfolio {
        return obj && 'portfolioId' in obj && 'artworks' in obj;
    }

    async function portfolioManagementApi(
        method: 'get' | 'create' | 'update',
        id?: number,
        dto?: any
    ): Promise<Portfolio | null> {
        const accessToken = await getAccessToken();
        if (!accessToken) throw new Error('Invalid access token');

        switch (method) {
            case 'get':
                return await get<Portfolio>(`portfolios/${id}`, accessToken);
            case 'create':
                return await postJson<Portfolio>("portfolios", accessToken, JSON.stringify(dto));
            case 'update':
                return await putJson<Portfolio>(`portfolios/${id}`, accessToken, JSON.stringify(dto));
            default: return null;
        }
    };

    useEffect(() => {
        if (selectedArtworks) {
            const artworkIds = selectedArtworks.map(a => a.artworkId);
            setSelectedArtworkIds(artworkIds);
        }
    }, [selectedArtworks]);

    async function load() {

        if (portfolio) {
            setError(null);
            setIsLoading(true);

            const id = portfolio.portfolioId;

            try {

                const data = await portfolioManagementApi("get", id);

                if (data && isPortfolio(data)) {
                    console.info("Load - 3");
                    setSelectedPortfolio(data);
                    setSelectedArtworks(data.artworks);

                    if (data.artworks) {
                        const artworkIds = data.artworks.map(a => a.artworkId);
                        setSelectedArtworkIds(artworkIds);
                    }
                }
            } catch (e: any) {
                setError(e?.response?.data?.detail ?? e?.message ?? "Failed to load portfolio details.");
            } finally {
                setIsLoading(false);
            }
        }
    }

    useEffect(() => { load(); }, [portfolio]);

    async function handleSaveAsync() {

        //Validate Form
        clearErrors();

        const isValid = validateRequired(name, "Portfolio Name", "A portfolio name is required");

        if (isValid) {

            const method = portfolio ? "update" : "create";
            const id = portfolio ? portfolio.portfolioId : 0;

            try {
                const dto: PortfolioUpdateDto = {
                    name: name,
                    description: description,
                    artworkIds: selectedArtworkIds,
                }

                const updated = await portfolioManagementApi(method, id, dto);

                if (updated) {
                    onSaved();
                    onClose();
                }
            } catch (e: any) {
                const data = e?.response?.data;
                if (data?.code === "LICENSE_LIMIT_EXCEEDED") {
                    setError(`${data.message} (${data.current}/${data.max})`);
                    return;
                }
                throw e;
            }
        }
    }


    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{portfolio ? "Edit Portfolio" : "Create Portfolio"}</DialogTitle>
            <DialogContent dividers>
                {/* Error Panel*/}
                <Box>
                    {error && (
                        <div style={{ marginTop: 12, padding: 12, border: "1px solid #ddd" }}>
                            {error}
                        </div>
                    )}
                    <ErrorSummary errors={errors} title="Portfolio Detail Errors" />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    <ArtworkPicker selected={selectedArtworkIds} onChange={setSelectedArtworkIds} expandView={true} artworkScope={setArtworks} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {portfolio && (
                        <Button variant="outlined" onClick={() => setAccessOpen(true)}>
                            Manage access
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveAsync}>Save</Button>
                </Box>
            </DialogActions>
        </Dialog>
            {portfolio && (
                <UserManagementAccessDialog
                  open={accessOpen}
                  onClose={() => setAccessOpen(false)}
                  scope={{ kind: "portfolio", clientId: activeClientId!, portfolioId: portfolio.portfolioId }}
                  onChanged={async () => { /* refresh */ }}
                />
            )}

        </>
    );
};

export default PortfolioDialog;
